import { Alert, Button, Card, Typography } from 'antd';
import React from 'react';
import { Link } from 'react-router-dom';
import Routes from '../../config/routes';
import { DUMMY_PLAYER_COUNT } from '../../helpers/StatisticsOpponentHelper';

const { Paragraph, Text, Title } = Typography;

interface GuideStep {
    title: string;
    body: React.ReactNode;
    action?: { text: string; link: string };
}

const steps: GuideStep[] = [
    {
        title: 'Add the competitions you want to track',
        body: (
            <>
                One competition per thing you follow: a league, a cup, or a single
                &quot;Friendlies&quot; competition for one-off games.
                <br />
                We suggest, for easier tracking, to name them{' '}
                <Text strong>country + league or cup + season</Text>, for example{' '}
                <Text code>Croatian League 25/26</Text>.
            </>
        ),
        action: { text: 'Go to Competitions', link: Routes.ROUTE_COMPETITIONS },
    },
    {
        title: 'Create your teams',
        body: (
            <>
                Add one team per category you track (first team, U19, and so on) under your club.
                The category you pick here is the one the team plays in.
                <br />
                Suggested naming: <Text strong>club + category + season</Text>, for example{' '}
                <Text code>Jug Dubrovnik First Team 25/26</Text>.
            </>
        ),
        action: { text: 'Go to Teams', link: Routes.ROUTE_TEAMS },
    },
    {
        title: 'Add your players to the team',
        body: (
            <>
                Open the team, go to the Players tab and add each player with name, surname, birth
                date, position and <Text strong>cap number</Text>. Cap numbers are what you tap
                during a match, so keep them correct.
            </>
        ),
    },
    {
        title: 'Enroll your team in the competition',
        body: (
            <>
                Open the competition, go to the Teams tab and add your team. This is what puts the
                team in the standings and in the match form for that competition.
            </>
        ),
    },
    {
        title: 'Add the opponent',
        body: (
            <>
                <Text strong>
                    You do not need to track the opposing club&apos;s/team&apos;s squads.
                </Text>
                <br />
                However, a match still needs players on both sides, so instead of typing in their
                roster you can create the opponent team once and let the app generate stand-in
                players for it.
                <br />
                On Teams press <Text strong>Add opponent</Text>, type the rival&apos;s name, pick
                the same category as your team and pick the competition to enroll them in, and leave{' '}
                <Text strong>Fill with {DUMMY_PLAYER_COUNT} placeholder players</Text> on. You get
                caps 1 to {DUMMY_PLAYER_COUNT} to tap for their goals and shots, and nothing to keep
                up to date.
                <br />
                Create each opponent once and reuse them for every match against them that season.
                Naming them like your own teams keeps the list tidy. If you do know their real
                players you can uncheck the box and enter them yourself, but it is never required.
            </>
        ),
        action: { text: 'Go to Teams', link: Routes.ROUTE_TEAMS },
    },
    {
        title: 'Create the match',
        body: (
            <>
                Easiest from the competition page, where the competition is already filled in. Pick
                home team, away team, the competition stage (use <Text code>Standings</Text> for a
                league and <Text code>Not relevant</Text> for a friendly), the start date and time,
                and the length of one period.
            </>
        ),
        action: { text: 'Go to Matches', link: Routes.ROUTE_MATCHES },
    },
    {
        title: 'Log the match',
        body: (
            <>
                Open the match and set both lineups: your real players on one side, the placeholders
                on the other. Then start the report and record events as the game goes: swim-off,
                shots, goals, exclusions, timeouts, goalkeeper changes.
            </>
        ),
    },
    {
        title: 'Read the statistics',
        body: (
            <>
                Everything is calculated from the events you logged, per player and per competition,
                so the more complete the log the better the numbers. Open{' '}
                <Text strong>Insights</Text> in the sidebar for your club or federation match list
                (charts come next).
            </>
        ),
        action: { text: 'Go to Insights', link: Routes.ROUTE_INSIGHTS },
    },
];

const StatisticsGuide: React.FC = () => (
    <div style={{ maxWidth: 900 }}>
        <Alert
            type="info"
            showIcon
            message="Your federation and club are already set up"
            description="Total Waterpolo creates the federation and your club for you. Everything below you do yourself, in this order."
            style={{ marginBottom: 24 }}
        />

        <Title level={4}>Getting started</Title>

        {steps.map(
            (step: GuideStep, index: number): React.ReactElement => (
                <Card key={step.title} size="small" style={{ marginBottom: 12 }}>
                    <Title level={5} style={{ marginBottom: 4 }}>
                        {index + 1}. {step.title}
                    </Title>
                    <Paragraph style={{ marginBottom: step.action ? 12 : 0 }}>
                        {step.body}
                    </Paragraph>
                    {step.action && (
                        <Link to={step.action.link}>
                            <Button type="primary" size="small">
                                {step.action.text}
                            </Button>
                        </Link>
                    )}
                </Card>
            )
        )}

        <Title level={4} style={{ marginTop: 24 }}>
            Good to know
        </Title>
        <ul>
            <li>
                A match report cannot be started without a goalkeeper on both sides, which is why
                opponents need at least the placeholder squad. Cap 1 of every placeholder squad is a
                goalkeeper.
            </li>
            <li>
                Home and away must be two different teams. If you play your own second team, create
                it as a separate team under your club.
            </li>
            <li>
                Placeholder players are named Opp 1 to Opp {DUMMY_PLAYER_COUNT}. They exist only so
                the opponent side of the match can be recorded, and they never affect your own
                players&apos; statistics.
            </li>
            <li>Start a new competition for each new season rather than reusing the old one.</li>
        </ul>
    </div>
);

export default StatisticsGuide;
